import { useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface AudioWaveformProps {
  audioContext: AudioContext | null;
  stream: MediaStream | null;
  isActive: boolean;
  isDarkMode: boolean;
  className?: string;
}

export const AudioWaveform = ({
  audioContext,
  stream,
  isActive,
  isDarkMode,
  className = '',
}: AudioWaveformProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const dataArrayRef = useRef<Uint8Array>();
  
  // Smoothing factor for the waveform (0.0 to 1.0, where 1.0 is maximum smoothing)
  const smoothing = 0.9;
  
  // Reset animation frame on unmount or when stream changes
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [stream]);
  
  // Get the current theme color
  const getThemeColor = useCallback(() => {
    return isDarkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)';
  }, [isDarkMode]);
  
  // Initialize audio analysis when component mounts or stream changes
  useEffect(() => {
    // Always clean up previous animation frame if it exists
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = undefined;
    }
    
    if (!isActive || !stream || !audioContext) {
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = undefined;
        }
      };
    }
    
    // Check if stream has audio tracks
    if (stream.getAudioTracks().length === 0) {
      console.warn('No audio tracks found in the provided stream');
      return;
    }

    // Create audio context if not provided
    const audioCtx = audioContext;
    if (!audioCtx) {
      console.error('No audio context provided');
      return;
    }
    
    // Create analyser node
    let analyser: AnalyserNode;
    try {
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = smoothing;
    } catch (error) {
      console.error('Error creating analyser:', error);
      return;
    }
    
    // Create media stream source
    let source: MediaStreamAudioSourceNode;
    try {
      source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);
    } catch (error) {
      console.error('Error creating audio source:', error);
      return;
    }
    
    // Store references
    analyserRef.current = analyser;
    sourceRef.current = source;
    
    // Initialize data array for frequency data
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    dataArrayRef.current = dataArray;
    
    // Start animation loop
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const draw = () => {
      if (!analyserRef.current || !dataArrayRef.current) return;
      
      animationRef.current = requestAnimationFrame(draw);
      
      const analyser = analyserRef.current;
      const dataArray = dataArrayRef.current;
      if (dataArray && analyser) {
        const byteData = new Uint8Array(dataArray.length);
        analyser.getByteFrequencyData(byteData);
        // Copy data back to our ref
        for (let i = 0; i < byteData.length; i++) {
          dataArray[i] = byteData[i];
        }
      }
      
      const WIDTH = canvas.width;
      const HEIGHT = canvas.height;
      
      ctx.clearRect(0, 0, WIDTH, HEIGHT);
      
      // Use solid color based on theme with full opacity
      const color = getThemeColor();
      
      // Draw the waveform with dynamic bar count based on width
      const barCount = Math.min(90, Math.floor(WIDTH / 4)); // Max 60 bars, min 4px per bar
      const barWidth = Math.max(2, (WIDTH / barCount) * 0.3); // 70% of available space for bars
      const barSpacing = (WIDTH / barCount) * 0.3; // 30% of space for spacing
      let x = 0;
      
      // Use solid color based on theme
      const fillStyle = getThemeColor();
      
      // Process audio data in chunks to match desired bar count
      const chunkSize = Math.floor(dataArray.length / barCount);
      
      for (let i = 0; i < barCount; i++) {
        // Calculate average height for this chunk
        let sum = 0;
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, dataArray.length);
        
        for (let j = start; j < end; j++) {
          sum += dataArray[j];
        }
        
        const avgHeight = sum / (end - start);
        const barHeight = (avgHeight / 255) * HEIGHT * 0.9; // 90% of max height
        
        // Skip very low bars for a cleaner look
        // if (barHeight < 2) {
        //   x += barWidth + barSpacing;
        //   continue;
        // }
        
        // Add some randomness to the height for a more organic feel
        const finalHeight = Math.max(barHeight, barHeight * 1);
        
        // Draw the bar with rounded corners
        const y = (HEIGHT - finalHeight) / 2;
        
        // Use theme color for the bars
        const radius = barWidth * .5;
        ctx.fillStyle = fillStyle;
        
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + barWidth - radius, y);
        ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
        ctx.lineTo(x + barWidth, y + finalHeight - radius);
        ctx.quadraticCurveTo(x + barWidth, y + finalHeight, x + barWidth - radius, y + finalHeight);
        ctx.lineTo(x + radius, y + finalHeight);
        ctx.quadraticCurveTo(x, y + finalHeight, x, y + finalHeight - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        ctx.fill();
        
        x += barWidth + barSpacing;
      }
    };
    
    // Start the animation
    draw();
    
    // Cleanup function
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
      }
      if (!audioContext) {
        // Only close the audio context if we created it
        audioCtx.close().catch(console.error);
      }
    };
  }, [stream, isActive, audioContext, getThemeColor, isDarkMode]);
  
  // Always render the canvas to prevent recreation
  return (
    <div className={`relative h-16 w-full overflow-hidden ${className}`}>
      <canvas
        ref={canvasRef}
        className="h-full w-full block"
        width={600}  // Increased width for better visualization
        height={64}  // Slightly taller for better visibility
      />
    </div>
  );
};